"use client"

import { useEffect, useState } from "react"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import {
  Calendar,
  Play,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { TrustBar } from "@/components/portfolio/trust-bar"
import type { PortfolioData } from "@/types/portfolio"

interface HeroProps {
  data: PortfolioData
}

export function Hero({ data }: HeroProps) {
  const { profile } = data

  const images = [
    "hero/h1.svg",
    "hero/h2.svg",
    "hero/h3.svg",
    "hero/h4.svg",
    "hero/3.png"
  ]

  const [currentImage, setCurrentImage] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length)
    }, 3500)

    return () => clearInterval(interval)
  }, [images.length])

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % images.length)
  }

  const previousImage = () => {
    setCurrentImage((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    )
  }

  return (
    <section className="relative overflow-hidden">
      {/* Fondo */}
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg,#f0fafa 0%,#ffffff 55%,#ffffff 100%)",
        }}
      />

      <div
        className="absolute top-0 right-0 w-[60%] h-[70%] -z-10 opacity-40 blur-3xl pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 20%, #00676E18, transparent 70%)",
        }}
      />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col justify-center pt-20 sm:pt-28 lg:pt-32 pb-0">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Texto */}
            <div className="flex flex-col items-start max-w-xl">
              <p className="text-sm font-semibold tracking-wide uppercase text-[#00676E] mb-6">
                Software dental · México
              </p>

              <h1 className="text-[2.75rem] sm:text-5xl xl:text-6xl font-semibold tracking-tight leading-[1.06] mb-8">
                El software dental que lleva tu clínica al{" "}
                <span className="text-[#00676E]">
                  siguiente nivel
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-muted-foreground leading-relaxed mb-10 max-w-lg">
                Agenda, expediente clínico, odontograma,
                cobros e inventario en un solo lugar.
                Sin instalaciones. Operativo en 24 horas
                con acompañamiento incluido.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Button
                  asChild
                  size="lg"
                  className="text-white h-14 px-10 text-base hover:opacity-90"
                  style={{
                    backgroundColor: "#F97316",
                  }}
                >
                  <a
                    href={profile.ctaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Solicita tu demo
                  </a>
                </Button>

                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="h-14 px-10 text-base border-[#00676E]/20"
                >
                  <a href="#funcionalidades">
                    <Play className="mr-2 h-4 w-4" />
                    Explorar plataforma
                  </a>
                </Button>
              </div>
            </div>

            {/* Carrusel */}
        <div className="hidden lg:flex flex-col items-center gap-6 relative"> {/* Cambiado a flex-col */}
          
          {/* Contenedor de Imagenes */}
          <div className="relative w-full max-w [760px] aspect-[16/10]">
            {images.map((image, index) => (
              <Image
                key={image}
                src={image}
                alt={`Pantalla ${index + 1}`}
                fill
                priority={index === 0}
                className={`absolute inset-0 object-contain transition-all duration-700 ${
                  currentImage === index ? "opacity-100 scale-100" : "opacity-0 scale-105"
                }`}
              />
            ))}

            {/* Flechas (se mantienen absolutas dentro de este div) */}
            <button
              onClick={previousImage}
              className="absolute left-5 top-1/2 -translate-y-1/2 bg-white/95 rounded-full shadow-lg p-3 hover:scale-105 transition"
            >
              <ChevronLeft size={22} className="text-[#00676E]" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-5 top-1/2 -translate-y-1/2 bg-white/95 rounded-full shadow-lg p-3 hover:scale-105 transition"
            >
              <ChevronRight size={22} className="text-[#00676E]" />
            </button>
          </div>

          {/* Indicadores (ahora fuera del contenedor de imágenes, debajo) */}
          <div className="flex gap-3">
            {images.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentImage(index)}
                className={`transition-all duration-300 rounded-full ${
                  currentImage === index
                    ? "w-8 h-2 bg-[#00676E]"
                    : "w-2 h-2 bg-gray-300" // Cambié bg-white/80 a gris para que se vea bien sobre fondo blanco
                }`}
              />
            ))}
          </div>
        </div>
          </div>
        </div>
      </div>

      <TrustBar className="mt-8 sm:mt-10 lg:mt-12" />
    </section>
  )
}